import * as React from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { Dropdown } from "semantic-ui-react";
import { isRequestActive } from "redux-request-loading";

import { CaseType } from "@teamprevent/common/model";

import { ConfirmModal } from "@teamprevent/common/confirmedButton/ConfirmModal";

import { ApplicationState } from "../../../applicationState";
import { customerSelector, routeIdSelector as customerIdSelector } from "../../customer/selectors";
import { routeIdSelector as employeeRecordIdSelector } from "../../employeeRecord/selectors";
import { AuthorizeProducts } from "../../../auth/AuthorizeProducts";
import { Customer } from "../../customer";
import { CREATE_BBV_CASE } from "../actiontypes";
import { createBbvCase as createBbvCaseAction } from "../actions";

import { OhCase } from "..";

import { Link, push } from "redux-little-router";

interface StateProps {
    customerId: string;
    employeeRecordId: string;
    customer: Customer;
    path: string;
    loading: boolean;
}

interface DispatchProps {
    createBbvCase: (customerId: string, employeeRecordId: string) => Promise<OhCase>;
    createHealthSurveillanceCase: (customerId: string, employeeRecordId: string) => void;
    navigateToCase: (customerId: string, employeeRecordId: string, ohCaseId: string) => void;
    createPhysiotherapyReferralCase: (customerId: string, employeeRecordId: string) => void;
}

type Props = StateProps & DispatchProps;

const CreateCaseButtonUnconnected: React.FunctionComponent<Props> = ({
    customerId,
    employeeRecordId,
    customer,
    path,
    loading,
    navigateToCase,
    createBbvCase: createBbvCaseFromProps,
    createHealthSurveillanceCase: createHealthSurveillanceCaseFromProps,
    createPhysiotherapyReferralCase: createPhysiotherapyReferralCaseFromProps
}) =>
{
    const [isBBVConfirmationModalOpen, setIsBBVConfirmationModalOpen] = React.useState<boolean>(false);

    const createBbvCase = React.useCallback(async () => {
        const newCase = await createBbvCaseFromProps(customerId, employeeRecordId);
        navigateToCase(customerId, employeeRecordId, newCase.id);
    }, [createBbvCaseFromProps, customerId, employeeRecordId, navigateToCase]);

    const openBBVConfirmationModal = React.useCallback(() => setIsBBVConfirmationModalOpen(true), []);
    const closeBBVConfirmationModal = React.useCallback(() => setIsBBVConfirmationModalOpen(false), []);

    const createHealthSurveillanceCase = React.useCallback(() => {
        createHealthSurveillanceCaseFromProps(customerId, employeeRecordId);
    }, [createHealthSurveillanceCaseFromProps, customerId, employeeRecordId]);

    const createPhysiotherapyReferralCase = React.useCallback(() => {
        createPhysiotherapyReferralCaseFromProps(customerId, employeeRecordId);
    }, [createPhysiotherapyReferralCaseFromProps, customerId, employeeRecordId]);

    return (
        <>
            <AuthorizeProducts
                customer={customer}
                productTypes={[
                    CaseType.IMMUNISATION,
                    CaseType.BBV,
                    CaseType.HEALTH_SURVEILLANCE,
                    CaseType.PPQ,
                    CaseType.REFERRAL,
                    CaseType.GENERIC,
                    CaseType.PHYSIOTHERAPY_REFERRAL,
                    CaseType.MHP_TREATMENT
                ]}
            >
                <Dropdown text="Create case" button direction="left" loading={loading}>
                    <Dropdown.Menu>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.IMMUNISATION}>
                            <Dropdown.Item text="Immunisation" as={Link} href={`${path}/createImmunisationCase`} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.BBV}>
                            <Dropdown.Item text="Blood Borne Virus" onClick={openBBVConfirmationModal} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.HEALTH_SURVEILLANCE}>
                            <Dropdown.Item text="Health Surveillance/Fit for Work" onClick={createHealthSurveillanceCase} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.PPQ}>
                            <Dropdown.Item text="Pre-Placement Questionnaire" as={Link} href={`${path}/createPpqCase`} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.REFERRAL}>
                            <Dropdown.Item text="Referral" as={Link} href={`${path}/createReferralCase`} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.GENERIC}>
                            <Dropdown.Item text="Generic" as={Link} href={`${path}/createGenericCase`} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.PHYSIOTHERAPY_REFERRAL}>
                            <Dropdown.Item text="Physiotherapy Referral" onClick={createPhysiotherapyReferralCase} />
                        </AuthorizeProducts>
                        <AuthorizeProducts customer={customer} productTypes={CaseType.MHP_TREATMENT}>
                            <Dropdown.Item text="MHP Treatment" href={`${path}/createMHPCase`} />
                        </AuthorizeProducts>
                    </Dropdown.Menu>
                </Dropdown>
            </AuthorizeProducts>
            <ConfirmModal
                isOpen={isBBVConfirmationModalOpen}
                promptText="Are you sure you want to create a BBV case?"
                headingText="Create BBV Case"
                confirmText="Create"
                onConfirm={createBbvCase}
                onClose={closeBBVConfirmationModal}
                confirmButtonColor="green"
            />
        </>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    return {
        customer: customerSelector(state),
        customerId: customerIdSelector(state),
        employeeRecordId: employeeRecordIdSelector(state),
        loading: isRequestActive(state, CREATE_BBV_CASE),
        path: state.router.pathname!
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => {
    return {
        createBbvCase: (customerId: string, employeeRecordId: string) => dispatch<Promise<OhCase>>(createBbvCaseAction(customerId, employeeRecordId)),
        navigateToCase: (customerId: string, employeeRecordId: string, ohCaseId: string) =>
            dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${ohCaseId}`)),
        createHealthSurveillanceCase: (customerId: string, employeeRecordId: string) =>
            dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/createHealthSurveillance`)),
        createPhysiotherapyReferralCase: (customerId: string, employeeRecordId: string) =>
            dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/createPhysiotherapyReferralCase`))
    };
};

export const CreateCaseButton = connect(mapStateToProps, mapDispatchToProps)(CreateCaseButtonUnconnected);
